/**
 * Tradução PT-BR e formatação de atributos e eventos do Traccar.
 *
 * Mantém duas tabelas (atributos e eventos) e uma função `formatAttributeValue`
 * que aplica unidades e semântica ao valor cru. Quando uma chave não estiver
 * mapeada, devolve a chave original como label e o valor toString — assim o
 * sistema sempre mostra algo, em vez de quebrar.
 */

export const ATTRIBUTE_LABELS: Record<string, string> = {
  // GPS / rede
  sat: 'Satélites GPS',
  satellites: 'Satélites GPS',
  hdop: 'Precisão HDOP',
  pdop: 'Precisão PDOP',
  vdop: 'Precisão VDOP',
  rssi: 'Sinal celular',
  operator: 'Operadora',
  cell: 'Célula',
  network: 'Rede',
  gsmSignal: 'Sinal GSM',

  // Estado da embarcação
  ignition: 'Em operação',
  motion: 'Em movimento',
  blocked: 'Bloqueio',
  charge: 'Carregando',
  charging: 'Carregando',
  alarm: 'Alarme',
  armed: 'Armada',
  status: 'Status',
  event: 'Código do evento',
  archive: 'Arquivado',
  result: 'Resultado',

  // Distância e movimento
  speed: 'Velocidade',
  course: 'Rumo',
  odometer: 'Odômetro',
  distance: 'Distância (este envio)',
  totalDistance: 'Distância total',

  // Energia
  battery: 'Tensão da bateria',
  batteryLevel: 'Nível da bateria',
  power: 'Tensão da fonte',

  // Motor / componentes da embarcação
  rpm: 'RPM',
  engineLoad: 'Carga do motor',
  engineTemp: 'Temperatura do motor',
  coolantTemp: 'Temperatura do líquido',
  hours: 'Horas trabalhadas',

  // Consumo
  fuel: 'Consumo',
  fuelLevel: 'Nível de consumo',
  fuelConsumption: 'Consumo médio',
  fuelUsed: 'Total consumido',

  // Operador
  driver: 'Operador',
  driverUniqueId: 'ID do operador',

  // Identificação
  vin: 'Número de série',
  protocol: 'Protocolo',
  raw: 'Pacote bruto',
  type: 'Tipo',

  // NautMax-specific (apelido fica em outro lugar, mas alguns servers usam attr)
  apelido: 'Apelido',
  nickname: 'Apelido',
  image: 'Imagem',
  photo: 'Foto',
  imageUrl: 'URL da imagem',
  photoUrl: 'URL da foto',
  deviceImage: 'Imagem do dispositivo',
};

export const EVENT_LABELS: Record<string, string> = {
  commandResult: 'Resposta do comando',
  deviceOnline: 'Embarcação conectada',
  deviceOffline: 'Embarcação desconectada',
  deviceUnknown: 'Sem comunicação',
  deviceInactive: 'Embarcação inativa',
  deviceCorrupted: 'Dados corrompidos',
  deviceMoving: 'Navegando',
  deviceStopped: 'Parada',
  deviceOverspeed: 'Excesso de velocidade',
  deviceFuelDrop: 'Queda no combustível',
  deviceFuelIncrease: 'Aumento no combustível',
  ignitionOn: 'Motor ligado',
  ignitionOff: 'Motor desligado',
  geofenceEnter: 'Entrou na área náutica',
  geofenceExit: 'Saiu da área náutica',
  alarm: 'Alarme',
  textMessage: 'Mensagem da embarcação',
  driverChanged: 'Tripulante trocado',
  maintenance: 'Manutenção',
  media: 'Mídia recebida',
  parking: 'Fundeada',
  bluetoothBeaconEnter: 'Entrou em beacon',
  bluetoothBeaconExit: 'Saiu de beacon',
  // Alertas náuticos derivados (gerados no app)
  rentalOverdue: 'Retorno de locação atrasado',
  docExpiring: 'Documento perto do vencimento',
  docExpired: 'Documento vencido',
  insuranceExpiring: 'Seguro perto do vencimento',
  insuranceExpired: 'Seguro vencido',
  marinaExit: 'Saída da marina',
};

/**
 * Tradução dos VALORES de alarme (atributo `alarm`/`alarms` do Traccar) para PT-BR náutico.
 * O evento é do tipo `alarm`; o valor específico (ex.: "jamming") vem no atributo. Cobre os
 * alarmes de segurança e casco reconhecidos pela plataforma (ver CATEGORY_RULES no backend).
 */
export const ALARM_LABELS: Record<string, string> = {
  general: 'Alarme geral',
  sos: 'SOS / Emergência',
  water: 'Água no porão',
  fuelLeak: 'Vazamento de combustível',
  powerCut: 'Corte de energia',
  powerOff: 'Desligamento',
  powerOn: 'Energia religada',
  lowBattery: 'Bateria baixa',
  lowPower: 'Tensão baixa',
  tow: 'Reboque',
  accident: 'Impacto / colisão',
  vibration: 'Vibração',
  movement: 'Movimento indevido',
  tampering: 'Violação do equipamento',
  removing: 'Rastreador removido',
  gpsAntennaCut: 'Antena GPS cortada',
  jamming: 'Bloqueio de sinal (jammer)',
  fault: 'Falha do equipamento',
  temperature: 'Temperatura fora da faixa',
  overspeed: 'Excesso de velocidade',
  door: 'Porta / escotilha',
  bonnet: 'Tampa do motor',
  highRpm: 'RPM alto',
  idle: 'Marcha lenta',
  geofenceEnter: 'Entrou na área',
  geofenceExit: 'Saiu da área',
  parking: 'Fundeada',
};

/** Tradução do tipo de evento; cai para a string original se não conhecida. */
export function translateEvent(type: string): string {
  return EVENT_LABELS[type] ?? type;
}

/** Tradução do valor de alarme; cai para a própria string se não conhecida. */
export function translateAlarm(value: string): string {
  return ALARM_LABELS[value] ?? value;
}

/**
 * Como o evento deve aparecer para o usuário.
 *
 * Todo alarme é do tipo `alarm`, e traduzir só o tipo mostra "Alarme" para qualquer um deles:
 * SOS, corte de energia e o alarme que a pessoa batizou saem com o mesmo rótulo. O que
 * distingue está em `attributes.alarm`, e é justamente o nome escolhido quando o alarme é
 * personalizado.
 *
 * Reclamado em 2026-08-10: o alarme "Boia porão" aparecia como "Alarme" em toda a interface.
 */
export function describeEvent(event: {
  type: string;
  attributes?: Record<string, unknown> | null;
}): string {
  if (event.type === 'alarm') {
    const alarme = event.attributes?.alarm;
    if (typeof alarme === 'string' && alarme.trim()) return translateAlarm(alarme.trim());
  }
  return translateEvent(event.type);
}

/** Tradução da chave de atributo; cai para a própria chave se não conhecida. */
export function translateAttribute(key: string): string {
  return ATTRIBUTE_LABELS[key] ?? key;
}

function metersToKmString(value: number): string {
  const km = value / 1000;
  return `${km.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} km`;
}

function msToHoursString(ms: number): string {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h.toLocaleString('pt-BR')}h ${String(m).padStart(2, '0')}min`;
}

/**
 * Formata o valor de um atributo conforme a chave: aplica unidades, converte
 * unidades brutas (m → km, ms → h, nós → km/h) e traduz booleanos com
 * semântica (ignition true → "Ligada"; blocked true → "Bloqueada").
 */
/** Chaves booleanas com rótulo próprio; o resto de booleano cai em Sim/Não. */
const BOOLEANO_COM_ROTULO: Record<string, [string, string]> = {
  ignition: ['Ligada', 'Desligada'],
  blocked: ['Bloqueada', 'Desbloqueada'],
  motion: ['Sim', 'Não'],
  charge: ['Sim', 'Não'],
  charging: ['Sim', 'Não'],
  archive: ['Sim', 'Não'],
  armed: ['Sim', 'Não'],
};

/** Chaves numéricas e como cada uma vira texto com unidade. */
const NUMERICO: Record<string, (n: number) => string> = {
  odometer: metersToKmString,
  distance: metersToKmString,
  totalDistance: metersToKmString,
  hours: msToHoursString,
  batteryLevel: (n) => `${Math.round(n)}%`,
  battery: (n) => `${n.toFixed(2)} V`,
  power: (n) => `${n.toFixed(2)} V`,
  speed: (n) => `${Math.round(n * 1.852)} km/h`,
  course: (n) => `${Math.round(n)}°`,
  rssi: (n) => `${n} dBm`,
  gsmSignal: (n) => `${n} dBm`,
  hdop: (n) => n.toFixed(2),
  pdop: (n) => n.toFixed(2),
  vdop: (n) => n.toFixed(2),
  engineTemp: (n) => `${n.toFixed(1)}°C`,
  coolantTemp: (n) => `${n.toFixed(1)}°C`,
  fuelLevel: (n) => `${n.toFixed(1)} L`,
  fuel: (n) => `${n.toFixed(1)} L`,
  fuelUsed: (n) => `${n.toFixed(1)} L`,
  fuelConsumption: (n) => `${n.toFixed(2)} L/h`,
  rpm: (n) => n.toLocaleString('pt-BR'),
  engineLoad: (n) => n.toLocaleString('pt-BR'),
};

const ehVerdadeiro = (v: unknown) => v === true || v === 'true';
const ehFalso = (v: unknown) => v === false || v === 'false';

/** Booleano com rótulo próprio, booleano puro, ou nada (deixa seguir para os números). */
function comoBooleano(key: string, value: unknown): string | null {
  const rotulos = BOOLEANO_COM_ROTULO[key];
  if (rotulos) {
    if (ehVerdadeiro(value)) return rotulos[0];
    if (ehFalso(value)) return rotulos[1];
  }
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return null;
}

/** Número com unidade conforme a chave, aceitando valor que veio como texto. */
function comoNumero(key: string, value: unknown): string | null {
  const formata = NUMERICO[key];
  if (!formata) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? formata(n) : null;
}

/**
 * Formata o valor de um atributo conforme a chave: unidades, conversões (m → km, ms → h,
 * nós → km/h) e booleanos com semântica (ignition true → "Ligada").
 *
 * As regras ficam nas duas tabelas acima porque acrescentar atributo é o que mais acontece aqui:
 * na forma anterior, cada novo sensor virava mais um `if` numa função que já tinha complexidade 55.
 */
export function formatAttributeValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';

  if (key === 'alarm' && typeof value === 'string') return translateAlarm(value);

  const booleano = comoBooleano(key, value);
  if (booleano !== null) return booleano;

  const numero = comoNumero(key, value);
  if (numero !== null) return numero;

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

/**
 * Agrupa eventos consecutivos do mesmo tipo, devolvendo um item por grupo
 * com `count` e o `serverTime` mais recente. Útil pra compactar listas longas
 * de deviceOnline/deviceOffline em pares.
 */
export interface GroupedEvent<E> {
  type: string;
  /** Como o grupo deve aparecer. Para alarme é o nome dele, não o rótulo do tipo. */
  label: string;
  count: number;
  first: E;
  last: E;
}

export function groupConsecutiveByType<
  E extends { type: string; attributes?: Record<string, unknown> | null; serverTime?: string },
>(events: E[]): GroupedEvent<E>[] {
  const groups: GroupedEvent<E>[] = [];
  for (const e of events) {
    // Agrupa pelo RÓTULO, não pelo tipo: senão "Boia porão" e "SOS" viram um grupo só de
    // "Alarme", e a lista passa a esconder justamente o que distingue um do outro.
    const label = describeEvent(e);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.count += 1;
      last.last = e;
    } else {
      groups.push({ type: e.type, label, count: 1, first: e, last: e });
    }
  }
  return groups;
}
